import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Plus, Edit, Trash2, CheckCircle, XCircle, Star, Percent, Save } from 'lucide-react';

interface PromotionalPictures {
  _id?: string;
  mainCard: {
    title: string;
    subtitle: string;
    imageUrl: string;
    rating: number;
  };
  secondaryCards: [
    {
      title: string;
      subtitle: string;
      imageUrl: string;
    },
    {
      title: string;
      subtitle: string;
      imageUrl: string;
    }
  ];
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

const AdminPromotionalPictures: React.FC = () => {
  const [pictures, setPictures] = useState<PromotionalPictures[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPictures, setCurrentPictures] = useState<PromotionalPictures | null>(null);
  const [formData, setFormData] = useState<PromotionalPictures>({
    mainCard: {
      title: '',
      subtitle: '',
      imageUrl: '',
          rating: 4.9
    },
    secondaryCards: [
      {
        title: '',
        subtitle: '',
        imageUrl: ''
      },
      {
        title: '',
        subtitle: '',
        imageUrl: ''
      }
    ],
    isActive: true
  });

  useEffect(() => {
    fetchPictures();
  }, []);

  const fetchPictures = async () => {
    setLoading(true);
    try {
      console.log('Fetching promotional pictures...');
      
      // Debug token on mobile
      const token = localStorage.getItem('token');
      const adminToken = localStorage.getItem('adminToken');
      console.log('Token exists:', !!token);
      console.log('Admin token exists:', !!adminToken);
      console.log('Current token:', token ? token.substring(0, 20) + '...' : 'No token');
      
      const response = await api.get('/api/promotional-pictures');
      console.log('Response:', response.data);
      setPictures(response.data);
    } catch (error: any) {
      console.error('Error fetching promotional pictures:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Check if it's a 401 error and suggest re-login
      if (error.response?.status === 401) {
        alert('انتهت صلاحية الجلسة. يرجى تسجيل الدخول مرة أخرى.');
        // Optionally redirect to login
        window.location.href = '/login';
      } else {
        alert(`فشل تحميل الصور الترويجية. ${error.response?.data?.message || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const numericValue = type === 'number' ? parseFloat(value) : value;
    
    if (name.startsWith('mainCard.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        mainCard: {
          ...prev.mainCard,
          [field]: numericValue
        }
      }));
    } else if (name.startsWith('secondaryCards.0.')) {
      const field = name.split('.')[2];
      setFormData(prev => ({
        ...prev,
        secondaryCards: [
          {
            ...prev.secondaryCards[0],
            [field]: value
          },
          prev.secondaryCards[1]
        ]
      }));
    } else if (name.startsWith('secondaryCards.1.')) {
      const field = name.split('.')[2];
      setFormData(prev => ({
        ...prev,
        secondaryCards: [
          prev.secondaryCards[0],
          {
            ...prev.secondaryCards[1],
            [field]: value
          }
        ]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
      }));
    }
  };

  const handleImageUpload = async (file: File, type: 'mainCard' | 'secondaryCard0' | 'secondaryCard1') => {
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await api.post('/api/products/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const imageUrl = response.data.imageUrl;
      
      if (type === 'mainCard') {
        setFormData(prev => ({
          ...prev,
          mainCard: {
            ...prev.mainCard,
            imageUrl
          }
        }));
      } else if (type === 'secondaryCard0') {
        setFormData(prev => ({
          ...prev,
          secondaryCards: [
            {
              ...prev.secondaryCards[0],
              imageUrl
            },
            prev.secondaryCards[1]
          ]
        }));
      } else if (type === 'secondaryCard1') {
        setFormData(prev => ({
          ...prev,
          secondaryCards: [
            prev.secondaryCards[0],
            {
              ...prev.secondaryCards[1],
              imageUrl
            }
          ]
        }));
      }
      
      alert('تم رفع الصورة بنجاح!');
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('فشل رفع الصورة.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (currentPictures) {
        await api.put(`/api/promotional-pictures/${currentPictures._id}`, formData);
        alert('تم تحديث الصور الترويجية بنجاح!');
      } else {
        await api.post('/api/promotional-pictures', formData);
        alert('تم إنشاء الصور الترويجية بنجاح!');
      }
      setIsModalOpen(false);
      fetchPictures();
    } catch (error) {
      console.error('Error saving promotional pictures:', error);
      alert('فشل حفظ الصور الترويجية.');
    }
  };

  const openCreateModal = () => {
    setCurrentPictures(null);
    setFormData({
      mainCard: {
      title: '',
      subtitle: '',
        imageUrl: '',
        rating: 4.9
      },
      secondaryCards: [
      {
        title: '',
        subtitle: '',
          imageUrl: ''
        },
      {
        title: '',
        subtitle: '',
          imageUrl: ''
        }
      ],
      isActive: true
    });
    setIsModalOpen(true);
  };

  const openEditModal = (pictures: PromotionalPictures) => {
    setCurrentPictures(pictures);
    setFormData(pictures);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('هل أنت متأكد أنك تريد حذف هذه الصور الترويجية؟')) {
      try {
        await api.delete(`/api/promotional-pictures/${id}`);
        alert('تم حذف الصور الترويجية بنجاح!');
        fetchPictures();
      } catch (error) {
        console.error('Error deleting promotional pictures:', error);
        alert('فشل حذف الصور الترويجية.');
      }
    }
  };

  const handleActivate = async (id: string) => {
    try {
      await api.put(`/api/promotional-pictures/${id}/activate`);
      alert('تم تفعيل الصور الترويجية بنجاح!');
      fetchPictures();
    } catch (error) {
      console.error('Error activating promotional pictures:', error);
      alert('فشل تفعيل الصور الترويجية.');
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-gradient-to-b from-pink-50 via-rose-50 to-pink-100 dark:bg-dark-bg dark:from-dark-bg dark:via-dark-bg dark:to-dark-bg min-h-screen transition-colors duration-300" dir="rtl">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600 dark:text-dark-textSecondary transition-colors duration-300">جاري التحميل...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-gradient-to-b from-pink-50 via-rose-50 to-pink-100 dark:bg-dark-bg dark:from-dark-bg dark:via-dark-bg dark:to-dark-bg min-h-screen transition-colors duration-300" dir="rtl">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text">إدارة الصور الترويجية</h1>
        <button
          onClick={openCreateModal}
          className="bg-primary-600 hover:bg-primary-700 text-white font-bold py-2 px-4 rounded-lg flex items-center transition-colors duration-300"
        >
          <Plus className="ml-2 h-5 w-5" />
          إضافة صور جديدة
        </button>
      </div>

      {/* Preview Section */}
      <div className="bg-gray-100 dark:bg-dark-surface p-6 rounded-lg mb-8 shadow-inner">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-dark-text mb-4">معاينة الصور النشطة</h2>
        {pictures.filter(p => p.isActive).length > 0 ? (
          <div className="grid grid-cols-2 gap-4">
            {/* Main Card Preview */}
            <div className="col-span-2 relative group">
              <div 
                className="aspect-square bg-gradient-to-br from-pink-200 to-rose-200 rounded-2xl shadow-2xl overflow-hidden"
                style={{
                  backgroundImage: pictures.find(p => p.isActive)?.mainCard.imageUrl ? `url(${pictures.find(p => p.isActive)?.mainCard.imageUrl})` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4" dir="rtl">
                  <h3 className="text-white font-bold text-lg mb-1">{pictures.find(p => p.isActive)?.mainCard.title}</h3>
                  <p className="text-white/90 text-sm">{pictures.find(p => p.isActive)?.mainCard.subtitle}</p>
                  
                </div>
              </div>
              
            </div>
            
            {/* Secondary Cards Preview */}
            <div className="relative group">
              <div 
                className="aspect-square bg-gradient-to-br from-pink-100 to-rose-100 rounded-xl shadow-lg overflow-hidden"
                style={{
                  backgroundImage: pictures.find(p => p.isActive)?.secondaryCards[0]?.imageUrl ? `url(${pictures.find(p => p.isActive)?.secondaryCards[0].imageUrl})` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
                <div className="absolute bottom-2 left-2 right-2" dir="rtl">
                  <h4 className="text-gray-800 dark:text-dark-text font-semibold text-sm">{pictures.find(p => p.isActive)?.secondaryCards[0]?.title}</h4>
                  <p className="text-gray-600 dark:text-dark-textSecondary text-xs">{pictures.find(p => p.isActive)?.secondaryCards[0]?.subtitle}</p>
                </div>
              </div>
            </div>
            
            <div className="relative group">
              <div 
                className="aspect-square bg-gradient-to-br from-pink-100 to-rose-100 rounded-xl shadow-lg overflow-hidden"
                style={{
                  backgroundImage: pictures.find(p => p.isActive)?.secondaryCards[1]?.imageUrl ? `url(${pictures.find(p => p.isActive)?.secondaryCards[1].imageUrl})` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
                <div className="absolute bottom-2 left-2 right-2" dir="rtl">
                  <h4 className="text-gray-800 dark:text-dark-text font-semibold text-sm">{pictures.find(p => p.isActive)?.secondaryCards[1]?.title}</h4>
                  <p className="text-gray-600 dark:text-dark-textSecondary text-xs">{pictures.find(p => p.isActive)?.secondaryCards[1]?.subtitle}</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-gray-600 dark:text-dark-textSecondary text-center">لا توجد صور ترويجية نشطة حالياً.</p>
        )}
      </div>

      {/* Pictures List */}
      <div className="bg-white dark:bg-dark-surface shadow-md rounded-lg overflow-hidden transition-colors duration-300">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
          <thead className="bg-gray-50 dark:bg-dark-hover">
            <tr>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-dark-textSecondary uppercase tracking-wider">
                العنوان الرئيسي
              </th>
              
              
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-dark-textSecondary uppercase tracking-wider">
                نشط
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-dark-textSecondary uppercase tracking-wider">
                الإجراءات
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-dark-surface divide-y divide-gray-200 dark:divide-dark-border">
            {pictures.map((picture) => (
              <tr key={picture._id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900 dark:text-dark-text">{picture.mainCard.title}</div>
                  <div className="text-sm text-gray-500 dark:text-dark-textSecondary">{picture.mainCard.subtitle}</div>
                </td>
                
                
                <td className="px-6 py-4 whitespace-nowrap">
                  {picture.isActive ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500" />
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => openEditModal(picture)}
                    className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-200 ml-4"
                  >
                    <Edit className="h-5 w-5" />
                  </button>
                  {!picture.isActive && (
                    <button
                      onClick={() => handleActivate(picture._id!)}
                      className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-200 ml-4"
                    >
                      <CheckCircle className="h-5 w-5" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(picture._id!)}
                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-200"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <div className="bg-white dark:bg-dark-surface p-8 rounded-lg shadow-xl max-w-4xl w-full mx-auto transition-colors duration-300" dir="rtl">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text mb-6">
              {currentPictures ? 'تعديل الصور الترويجية' : 'إضافة صور ترويجية جديدة'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Main Card */}
              <div className="bg-gray-50 dark:bg-dark-hover p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-4">البطاقة الرئيسية</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-2">
                      العنوان
                    </label>
                    <input
                      type="text"
                      name="mainCard.title"
                      value={formData.mainCard.title}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-2">
                      العنوان الفرعي
                    </label>
                    <input
                      type="text"
                      name="mainCard.subtitle"
                      value={formData.mainCard.subtitle}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-2">
                      صورة البطاقة الرئيسية
                    </label>
                    <div className="flex items-center space-x-4">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleImageUpload(file, 'mainCard');
                        }}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                      />
                      {formData.mainCard.imageUrl && (
                        <img src={formData.mainCard.imageUrl} alt="Main card preview" className="w-16 h-16 object-cover rounded-lg" />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Secondary Cards */}
              <div className="bg-gray-50 dark:bg-dark-hover p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-4">البطاقات الثانوية</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Secondary Card 1 */}
                  <div>
                    <h4 className="text-md font-medium text-gray-800 dark:text-dark-text mb-3">البطاقة الأولى</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-1">
                          العنوان
                        </label>
                        <input
                          type="text"
                          name="secondaryCards.0.title"
                          value={formData.secondaryCards[0].title}
                          onChange={handleInputChange}
                          className="w-full p-2 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-1">
                          العنوان الفرعي
                        </label>
                        <input
                          type="text"
                          name="secondaryCards.0.subtitle"
                          value={formData.secondaryCards[0].subtitle}
                          onChange={handleInputChange}
                          className="w-full p-2 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-1">
                          الصورة
                        </label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleImageUpload(file, 'secondaryCard0');
                            }}
                            className="block w-full text-sm text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                          />
                          {formData.secondaryCards[0].imageUrl && (
                            <img src={formData.secondaryCards[0].imageUrl} alt="Secondary card 1 preview" className="w-12 h-12 object-cover rounded" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Secondary Card 2 */}
                  <div>
                    <h4 className="text-md font-medium text-gray-800 dark:text-dark-text mb-3">البطاقة الثانية</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-1">
                          العنوان
                        </label>
                        <input
                          type="text"
                          name="secondaryCards.1.title"
                          value={formData.secondaryCards[1].title}
                          onChange={handleInputChange}
                          className="w-full p-2 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-1">
                          العنوان الفرعي
                        </label>
                        <input
                          type="text"
                          name="secondaryCards.1.subtitle"
                          value={formData.secondaryCards[1].subtitle}
                          onChange={handleInputChange}
                          className="w-full p-2 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-1">
                          الصورة
                        </label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleImageUpload(file, 'secondaryCard1');
                            }}
                            className="block w-full text-sm text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                          />
                          {formData.secondaryCards[1].imageUrl && (
                            <img src={formData.secondaryCards[1].imageUrl} alt="Secondary card 2 preview" className="w-12 h-12 object-cover rounded" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-lg transition-colors duration-300"
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="bg-primary-600 hover:bg-primary-700 text-white font-bold py-2 px-4 rounded-lg flex items-center transition-colors duration-300"
                >
                  <Save className="ml-2 h-5 w-5" />
                  {currentPictures ? 'تحديث' : 'إضافة'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPromotionalPictures;



